/**
 * Protected Route Komponente
 *
 * Schützt Routes, die nur für authentifizierte User zugänglich sein sollen.
 * Zeigt Loading-State während User-Daten geladen werden.
 * Redirected zu /login wenn nicht authentifiziert.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useSupabaseAuth();
  const location = useLocation();

  // Loading-State während User-Daten geladen werden
  if (isLoading) {
    return (
      <main className="flex items-center justify-center min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Authentifizierung wird geprüft...
          </p>
        </div>
      </main>
    );
  }

  // Nicht authentifiziert → Redirect zu Login
  if (!isAuthenticated) {
    return <Navigate to={`/v2/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Authentifiziert → Zeige geschützten Content
  return <>{children}</>;
}

